import React from "react";
import styles from "./StickMire.module.css";

const SECTEURS = 64;          // découpage du tour pour mémoriser jusqu'où le stick est allé dans chaque direction
const TRACE = 40;             // nombre de positions gardées pour la traînée
const DELAI_REPOS = 500;      // ms : le stick est « au repos » quand sa position n'a pas bougé depuis ce délai
const SEUIL_MOUVEMENT = 0.02; // un déplacement de plus de 2 % de la course compte comme un mouvement
const DERIVE_MAX = 0.35;      // au repos au-delà de cet écart (ou de la tolérance + 15 points), le stick est poussé, pas en dérive

const pct = v => (Math.round(v * 100) === 0 ? "0" : (v > 0 ? "+" : "−") + Math.abs(Math.round(v * 100))) + " %";

/**
 * Zone morte circulaire (zone en fraction : 0,05 = 5 %), comme une frontière :
 * - le stick à moins de `zone` du centre est montré exactement au centre : le point ne bouge pas ;
 * - dès qu'il la dépasse, le point apparaît à sa vraie position, donc en dehors du cercle.
 * Le point ne se déplace jamais à l'intérieur du cercle. Rend [x, y].
 */
export function zoneMorte(x, y, zone) {
    return Math.hypot(x, y) < zone ? [0, 0] : [x, y];
}

const borne = (v, max) => Math.max(-max, Math.min(max, v));

/** Vrai quand la position (x, y) n'a pas bougé depuis DELAI_REPOS ; faux dès qu'elle bouge. */
function useRepos(x, y) {
    const reference = React.useRef({x, y});
    const minuteur = React.useRef(null);
    const [repos, setRepos] = React.useState(true);

    React.useEffect(() => {
        if (Math.hypot(x - reference.current.x, y - reference.current.y) <= SEUIL_MOUVEMENT) return;
        reference.current = {x, y};
        setRepos(false);
        window.clearTimeout(minuteur.current);
        minuteur.current = window.setTimeout(() => setRepos(true), DELAI_REPOS);
    }, [x, y]);

    React.useEffect(() => () => window.clearTimeout(minuteur.current), []);
    return repos;
}

/**
 * Mire d'un stick : position en direct, étendue atteinte (circularité) et zoom ×5 sur le centre,
 * pour constater précisément qu'une dérive a disparu.
 *
 * @param titre  « Stick gauche » / « Stick droit »
 * @param x, y   position AFFICHÉE du stick (après zone morte), de -1 à 1 (y positif vers le bas)
 * @param reel   [x, y] position réelle du stick, avant zone morte : sert au verdict (par défaut x, y)
 * @param zoneMorte  tolérance de dérive en % du centre : un stick au repos à moins de cet écart est « centré »,
 *                   comme sur une console qui ramène ce petit écart à 0 %
 * @param zoom   grossissement ×N de la zone centrale (réglages du tableau de bord)
 * @param apercu aperçu du tableau de bord : dessine la tolérance sur la mire principale et montre l'écart réel
 *               (l'écran client, lui, n'affiche jamais l'écart tant que le stick est dans la tolérance)
 */
export default function StickMire({titre, x, y, reel, zoneMorte, zoom, apercu = false}) {
    const tolerance = zoneMorte / 100;
    const [rx, ry] = reel || [x, y];
    const ecartReel = Math.hypot(rx, ry);
    const repos = useRepos(rx, ry);
    const trace = React.useRef([]);
    const portee = React.useRef(new Array(SECTEURS).fill(0));
    const derniere = React.useRef("");

    // Mémoire de la traînée et de l'étendue atteinte ; elle repart de zéro quand la manette est débranchée
    // (l'écran démonte alors la mire).
    const cle = x + "," + y;
    if (cle !== derniere.current) {
        derniere.current = cle;
        trace.current = trace.current.concat([{x, y}]).slice(-TRACE);
        const r = Math.hypot(x, y);
        if (r > 0.4) {
            const i = Math.min(SECTEURS - 1, Math.floor(((Math.atan2(y, x) + Math.PI) / (2 * Math.PI)) * SECTEURS));
            portee.current[i] = Math.max(portee.current[i], Math.min(r, 1.05));
        }
    }

    // Verdict sur la vraie position : dans la tolérance → centré ; immobile au-dessus → dérive ; sinon en mouvement
    let etat = "mouvement", message = "En mouvement";
    if (ecartReel === 0 || ecartReel < tolerance) {
        etat = "ok";
        message = "Centré ✔";
    } else if (repos && ecartReel <= Math.max(DERIVE_MAX, tolerance + 0.15)) {
        etat = "ecart";
        message = "Dérive de " + Math.round(ecartReel * 100) + " % (tolérance " + zoneMorte + " %)";
    }

    const centres = portee.current
        .map((r, i) => r > 0.4 && {r, a: ((i + 0.5) / SECTEURS) * 2 * Math.PI - Math.PI})
        .filter(Boolean);

    return (
        <div className={styles.carte}>
            <h2 className={styles.titre}>{titre}</h2>

            <svg viewBox="-112 -112 224 224" className={styles.mire} role="img" aria-label={"Mire du " + titre.toLowerCase()}>
                <circle r="100" className={styles.plateau}/>
                {[25, 50, 75].map(r => <circle key={r} r={r} className={styles.anneau}/>)}
                <line x1="-100" x2="100" className={styles.axe}/>
                <line y1="-100" y2="100" className={styles.axe}/>
                <circle r="100" className={styles.bord}/>

                {apercu && <circle r={tolerance * 100} className={styles.tolerance}/>}

                {/* jusqu'où le stick est allé dans chaque direction : un cercle régulier = stick sain */}
                {centres.map((c, i) => (
                    <circle key={i} cx={Math.cos(c.a) * c.r * 100} cy={Math.sin(c.a) * c.r * 100} r="2.2" className={styles.portee}/>
                ))}

                {trace.current.map((p, i, tout) => (
                    <circle key={i} cx={borne(p.x, 1.08) * 100} cy={borne(p.y, 1.08) * 100} r="2.5"
                            className={styles.trace} style={{opacity: ((i + 1) / tout.length) * 0.5}}/>
                ))}

                <circle cx={borne(x, 1.08) * 100} cy={borne(y, 1.08) * 100} r="4.5" className={styles["point_" + etat]}/>
            </svg>

            <div className={styles.bas}>
                <svg viewBox="-112 -112 224 224" className={styles.zoom} role="img" aria-label="Zoom sur le centre">
                    <rect x="-104" y="-104" width="208" height="208" rx="10" className={styles.plateau}/>
                    {[-75, -50, -25, 25, 50, 75].map(v => (
                        <g key={v}>
                            <line x1={v} x2={v} y1="-104" y2="104" className={styles.anneau}/>
                            <line y1={v} y2={v} x1="-104" x2="104" className={styles.anneau}/>
                        </g>
                    ))}
                    <line x1="-104" x2="104" className={styles.axe}/>
                    <line y1="-104" y2="104" className={styles.axe}/>
                    <circle r={Math.min(100, tolerance * zoom * 100)} className={styles.tolerance}/>
                    <circle cx={borne(x * zoom * 100, 100)} cy={borne(y * zoom * 100, 100)} r="4.5" className={styles["point_" + etat]}/>
                </svg>

                <div className={styles.mesure}>
                    <div className={styles["verdict_" + etat]}>{message}</div>
                    {apercu && (
                        <div className={styles.valeurs}>
                            <span>X {pct(x)}</span>
                            <span>Y {pct(y)}</span>
                            <span>Écart réel {Math.round(ecartReel * 100)} %</span>
                        </div>
                    )}
                    <div className={styles.legende}>Zoom ×{zoom} au centre.<br/>Tolérance de dérive de {zoneMorte} % : relâchez le stick, le point doit revenir dans le cercle vert.</div>
                </div>
            </div>
        </div>
    );
}
